import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography
} from '@dtx-company/design-system/src'
import { PaymentMethod } from '@stripe/stripe-js'
import { PaymentMethodDisplay } from '@dtx-company/app/shared/common/src/components/PlanPurchaseExperience/components/PaymentMethodDisplay'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { confetti } from '../../utils/confetti'
import { getPlanTypeName } from '../../utils/getPlanTypeName'
import { isOrgPlan } from '@dtx-company/app/shared/common/src/utils/isOrgPlan'
import { useEffect, useRef } from 'react'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'

interface UpgradeSuccessModalProps {
  open: boolean
  onClose: () => void
  planType: PlanType | undefined
  paymentMethod: PaymentMethod | undefined
}

function getCTAButtonText(planType: PlanType): string {
  if (isOrgPlan(planType)) {
    return 'Start collaborating'
  }
  return 'Done'
}

export const UpgradeSuccessModal = ({
  open,
  onClose,
  planType = PlanType.PRO,
  paymentMethod
}: UpgradeSuccessModalProps): JSX.Element => {
  const confettiRootRef = useRef<HTMLDivElement | null>(null)
  const planTypeName = getPlanTypeName(planType)
  const ctaButtonText = getCTAButtonText(planType)

  useEffect(() => {
    if (!open) return

    let request: number

    function explode(): void {
      // wait for the element to be available
      if (!confettiRootRef.current) {
        request = window.requestAnimationFrame(explode)
        return
      }
      confetti(confettiRootRef.current, {
        angle: 120,
        spread: 296,
        startVelocity: 30,
        elementCount: 340,
        dragFriction: 0.12,
        stagger: 4,
        duration: 7000,
        perspective: '575px'
      })
    }

    request = window.requestAnimationFrame(explode)

    return () => {
      window.cancelAnimationFrame(request)
    }
  }, [open])

  return (
    <Dialog
      fullWidth={true}
      PaperProps={{
        sx: { maxWidth: 434 }
      }}
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
          onClose()
        }
      }}
      showCloseIcon
    >
      <DialogContent>
        <Stack
          data-testid={'update-success-modal-window'}
          flexDirection="column"
          alignItems="center"
          sx={theme => ({
            height: '100%',
            width: '100%',
            maxWidth: { xs: theme.spacing(160), md: theme.spacing(108) },
            m: '0 auto',
            bg: 'primary.white',
            overflowY: 'auto'
          })}
        >
          <Stack
            textAlign="center"
            alignItems="center"
            zIndex={2}
            sx={{
              width: '100%'
            }}
            gap={10}
          >
            <Typography variant="headlineSmall">You're all set!</Typography>
            <CheckCircleOutlinedIcon
              sx={theme => ({
                width: theme.spacing(16),
                height: theme.spacing(16),
                color: 'success.main'
              })}
            />
            <Stack gap={5}>
              <Typography component="p" variant="headlineXSmall">
                You've been upgraded to {planTypeName}!
              </Typography>
              <Typography component="p" variant="bodyMedium">
                Your order was successfully processed using
              </Typography>
            </Stack>
            {paymentMethod && <PaymentMethodDisplay paymentMethod={paymentMethod} mode={'short'} />}
            <Button onClick={onClose} size="large">
              {ctaButtonText}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          pointerEvents: 'none'
        }}
      >
        <Box
          ref={confettiRootRef}
          sx={{
            position: 'absolute',
            top: '40%',
            right: '50%',
            zIndex: 1000
          }}
        />
      </Box>
    </Dialog>
  )
}
